import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import { FaLink, FaPen, FaPlus, FaSearch, FaTimes } from "react-icons/fa";

import api from "services/api";
import { useToast } from "hooks/toast";
import useDebounce from "hooks/useDebounce";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import MetricCard from "components/MetricCard";
import MetricCardGroup from "components/MetricCardGroup";
import ShellCard, { TableSurface } from "components/ShellCard";
import SortHeader from "components/SortHeader";
import TablePagination from "components/TablePagination";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { exportCsv } from "utils/exportCsv";
import { paginate, sortRows, toggleSort } from "utils/tableHelpers";
import { reactSelectPortalProps, reactSelectStyles } from "utils/reactSelectStyles";
import {
  ConfirmModal,
  ListState,
  Pill,
  STATUS_ALL,
  StatusPill,
  isActive,
  matchesStatus,
  mutedStyle,
  normalizeText,
  toastError,
  toastSuccess,
} from "../shared";

// Taxas de frete (Cadastros Gerais) no padrão do Hub desde a sprint 638. A
// regra e a API não mudaram: cadastro global da taxa, vínculo com os
// componentes recebidos no CT-e e, para PERCENTUAL/COMPOSTA, a base de cálculo.

const URL_BASE = "/cadastros/taxas-frete";
const TIPO_POR = {
  ACADA: ["KG", "KM", "VOLUME"],
  FIXO: ["CTE", "NF"],
  // PO1-3035: COMPOSTA = base composta (custo do frete + soma de outras taxas
  // vinculadas na aba "Base de cálculo", ex. GRIS + Pedágio).
  // PO1-3073: TOTAL = custo do frete + todas as outras taxas da linha, de
  // qualquer tipo (ex. ADICIONAL FRETE do reajuste emergencial).
  PERCENTUAL: ["FRETE", "NF", "AVERBADA", "COMPOSTA", "TOTAL"],
};
const TIPO_LABEL = { ACADA: "A cada", FIXO: "Fixo", PERCENTUAL: "Percentual" };

const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: "Todas" },
  { value: "active", label: "Ativas" },
  { value: "inactive", label: "Inativas" },
];
const TIPO_OPTIONS = [{ value: "", label: "Todos" }].concat(
  Object.keys(TIPO_POR).map((tipo) => ({ value: tipo, label: TIPO_LABEL[tipo] }))
);
const COMPONENTES_OPTIONS = [
  { value: "", label: "Todas" },
  { value: "com", label: "Com componente do CT-e" },
  { value: "sem", label: "Sem componente do CT-e" },
];
const DEFAULT_SORT = { field: "codigo", dir: "asc" };

function emptyForm() {
  return {
    codigo: "",
    nome: "",
    descricao: "",
    tipo: "",
    por: "",
    generalidade: false,
    aplicaSobreTipoVeiculo: false,
    status: true,
    dataValidadeInicio: "",
    dataValidadeFim: "",
  };
}

function dateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function dataBr(value) {
  const iso = dateInput(value);
  if (!iso) return "";
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

function hojeIso() {
  return new Date().toISOString().slice(0, 10);
}

function vencida(item) {
  const fim = dateInput(item.dataValidadeFim);
  return Boolean(fim) && fim < hojeIso();
}

function regraLabel(item) {
  const tipo = TIPO_LABEL[item.tipo] || item.tipo || "";
  return item.por ? `${tipo} · ${item.por}` : tipo;
}

function sortValue(row, field) {
  if (field === "status") return isActive(row.status) ? 1 : 0;
  if (field === "regra") return regraLabel(row);
  if (field === "quantidadeComponentes" || field === "quantidadeTabelas") return Number(row[field]) || 0;
  return row[field];
}

const iconButtonStyle = {
  width: 32,
  height: 32,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const TaxasFrete = () => {
  const { addToast } = useToast();
  const [taxas, setTaxas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [tipoFilter, setTipoFilter] = useState("");
  const [componentesFilter, setComponentesFilter] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(search, 250);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState("dados");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toToggle, setToToggle] = useState(null);
  const [toggling, setToggling] = useState(false);

  const [linked, setLinked] = useState([]);
  const [linkedLoading, setLinkedLoading] = useState(false);
  const [componentSearch, setComponentSearch] = useState("");
  const debouncedComponentSearch = useDebounce(componentSearch, 300);
  const [candidates, setCandidates] = useState([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [mappingBusyId, setMappingBusyId] = useState(null);

  // PO1-3035: aba "Base de cálculo" (só para PERCENTUAL/COMPOSTA) — mesmo
  // padrão de estado da aba "Componentes do CT-e", endpoint /base-calculo.
  const [linkedBase, setLinkedBase] = useState([]);
  const [linkedBaseLoading, setLinkedBaseLoading] = useState(false);
  const [baseSearch, setBaseSearch] = useState("");
  const debouncedBaseSearch = useDebounce(baseSearch, 300);
  const [candidatesBase, setCandidatesBase] = useState([]);
  const [candidateBaseLoading, setCandidateBaseLoading] = useState(false);
  const [baseMappingBusyId, setBaseMappingBusyId] = useState(null);
  const isComposta = form.tipo === "PERCENTUAL" && form.por === "COMPOSTA";

  const loadTaxas = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const result = await api.get(URL_BASE);
      setTaxas(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      setTaxas([]);
      setLoadError("Não foi possível buscar as taxas de frete.");
      toastError(addToast, "Erro ao carregar", error, "Não foi possível buscar as taxas de frete.");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadTaxas();
  }, [loadTaxas]);

  const totais = useMemo(() => {
    const ativas = taxas.filter((item) => isActive(item.status));
    return {
      total: taxas.length,
      ativas: ativas.length,
      semComponente: ativas.filter((item) => !Number(item.quantidadeComponentes)).length,
      emTabelas: taxas.filter((item) => Number(item.quantidadeTabelas) > 0).length,
    };
  }, [taxas]);

  const filtered = useMemo(() => {
    const query = normalizeText(debouncedSearch);
    return taxas.filter((item) => {
      if (!matchesStatus(item.status, statusFilter)) return false;
      if (tipoFilter && item.tipo !== tipoFilter) return false;
      if (componentesFilter === "com" && !Number(item.quantidadeComponentes)) return false;
      if (componentesFilter === "sem" && Number(item.quantidadeComponentes)) return false;
      if (!query) return true;
      return normalizeText([item.codigo, item.nome, item.descricao, item.tipo, item.por].join(" ")).includes(query);
    });
  }, [componentesFilter, debouncedSearch, statusFilter, taxas, tipoFilter]);

  const sorted = useMemo(() => sortRows(filtered, sort, sortValue), [filtered, sort]);
  const paged = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, tipoFilter, componentesFilter, sort, pageSize]);

  const loadLinked = useCallback(
    async (taxaId) => {
      if (!taxaId) return;
      try {
        setLinkedLoading(true);
        const result = await api.get(`${URL_BASE}/${taxaId}/componentes`, { params: { vinculado: true } });
        setLinked(Array.isArray(result.data) ? result.data : []);
      } catch (error) {
        setLinked([]);
        toastError(addToast, "Erro ao carregar vínculos", error, "Não foi possível buscar os componentes vinculados.");
      } finally {
        setLinkedLoading(false);
      }
    },
    [addToast]
  );

  useEffect(() => {
    if (!modalOpen || activeTab !== "componentes" || !editing) return;
    loadLinked(editing.id);
  }, [activeTab, editing, loadLinked, modalOpen]);

  useEffect(() => {
    if (!modalOpen || activeTab !== "componentes" || !editing || debouncedComponentSearch.trim().length < 2) {
      setCandidates([]);
      return undefined;
    }
    let active = true;
    setCandidateLoading(true);
    api
      .get(`${URL_BASE}/${editing.id}/componentes`, {
        params: { busca: debouncedComponentSearch.trim(), status: true, vinculado: false },
      })
      .then((result) => {
        if (active) setCandidates(Array.isArray(result.data) ? result.data : []);
      })
      .catch((error) => {
        if (active) toastError(addToast, "Erro na pesquisa", error, "Não foi possível pesquisar componentes.");
      })
      .finally(() => {
        if (active) setCandidateLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activeTab, addToast, debouncedComponentSearch, editing, modalOpen]);

  const loadLinkedBase = useCallback(
    async (taxaId) => {
      if (!taxaId) return;
      try {
        setLinkedBaseLoading(true);
        const result = await api.get(`${URL_BASE}/${taxaId}/base-calculo`, { params: { vinculado: true } });
        setLinkedBase(Array.isArray(result.data) ? result.data : []);
      } catch (error) {
        setLinkedBase([]);
        toastError(addToast, "Erro ao carregar vínculos", error, "Não foi possível buscar as taxas vinculadas à base.");
      } finally {
        setLinkedBaseLoading(false);
      }
    },
    [addToast]
  );

  useEffect(() => {
    if (!modalOpen || activeTab !== "baseCalculo" || !editing) return;
    loadLinkedBase(editing.id);
  }, [activeTab, editing, loadLinkedBase, modalOpen]);

  useEffect(() => {
    if (!modalOpen || activeTab !== "baseCalculo" || !editing || debouncedBaseSearch.trim().length < 2) {
      setCandidatesBase([]);
      return undefined;
    }
    let active = true;
    setCandidateBaseLoading(true);
    api
      .get(`${URL_BASE}/${editing.id}/base-calculo`, {
        params: { busca: debouncedBaseSearch.trim(), status: true, vinculado: false },
      })
      .then((result) => {
        if (active) setCandidatesBase(Array.isArray(result.data) ? result.data : []);
      })
      .catch((error) => {
        if (active) toastError(addToast, "Erro na pesquisa", error, "Não foi possível pesquisar taxas.");
      })
      .finally(() => {
        if (active) setCandidateBaseLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activeTab, addToast, debouncedBaseSearch, editing, modalOpen]);

  const resetModalLists = () => {
    setLinked([]);
    setCandidates([]);
    setComponentSearch("");
    setLinkedBase([]);
    setCandidatesBase([]);
    setBaseSearch("");
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setErrors({});
    setActiveTab("dados");
    resetModalLists();
    setModalOpen(true);
  };

  const openEdit = (item, tab = "dados") => {
    setEditing(item);
    setForm({
      codigo: item.codigo || "",
      nome: item.nome || "",
      descricao: item.descricao || "",
      tipo: item.tipo || "",
      por: item.por || "",
      generalidade: Boolean(item.generalidade),
      aplicaSobreTipoVeiculo: Boolean(item.aplicaSobreTipoVeiculo),
      status: isActive(item.status),
      dataValidadeInicio: dateInput(item.dataValidadeInicio),
      dataValidadeFim: dateInput(item.dataValidadeFim),
    });
    setErrors({});
    setActiveTab(tab);
    resetModalLists();
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving || mappingBusyId || baseMappingBusyId) return;
    setModalOpen(false);
  };

  const updateForm = (field, value) => {
    setForm((current) => {
      if (field === "tipo") {
        const allowed = TIPO_POR[value] || [];
        return { ...current, tipo: value, por: allowed.includes(current.por) ? current.por : "" };
      }
      return { ...current, [field]: value };
    });
    if (field === "tipo" || field === "por") {
      setActiveTab((current) => {
        const aindaComposta =
          (field === "tipo" ? value : form.tipo) === "PERCENTUAL" && (field === "por" ? value : form.por) === "COMPOSTA";
        return current === "baseCalculo" && !aindaComposta ? "dados" : current;
      });
    }
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (!form.codigo.trim()) next.codigo = "Informe o código.";
    if (!form.nome.trim()) next.nome = "Informe o nome.";
    if (!form.tipo) next.tipo = "Selecione o tipo.";
    if (!form.por) next.por = "Selecione a incidência.";
    if (form.dataValidadeInicio && form.dataValidadeFim && form.dataValidadeInicio > form.dataValidadeFim) {
      next.dataValidadeFim = "A validade final deve ser posterior à inicial.";
    }
    return next;
  };

  const save = async () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form, codigo: form.codigo.trim(), nome: form.nome.trim(), descricao: form.descricao.trim() };
      if (editing) {
        await api.put(`${URL_BASE}/${editing.id}`, payload);
      } else {
        const result = await api.post(URL_BASE, payload);
        // A taxa nova fica aberta para vincular os componentes do CT-e em seguida.
        setEditing({ ...payload, id: result.data.id });
      }
      toastSuccess(
        addToast,
        editing ? "Taxa atualizada" : "Taxa cadastrada",
        editing ? "As alterações foram salvas." : "A taxa foi criada; os componentes já podem ser vinculados."
      );
      await loadTaxas();
    } catch (error) {
      toastError(addToast, "Erro ao salvar", error, "Não foi possível salvar a taxa de frete.");
    } finally {
      setSaving(false);
    }
  };

  const applyToggle = async (item) => {
    const ativar = !isActive(item.status);
    try {
      setToggling(true);
      await api.patch(`${URL_BASE}/${item.id}/status`, { status: ativar });
      toastSuccess(addToast, ativar ? "Taxa ativada" : "Taxa inativada", `A taxa "${item.nome}" está ${ativar ? "ativa" : "inativa"}.`);
      setToToggle(null);
      await loadTaxas();
    } catch (error) {
      toastError(addToast, "Erro ao alterar status", error, "Não foi possível alterar o status da taxa.");
      setToToggle(null);
    } finally {
      setToggling(false);
    }
  };

  // Inativar pede confirmação (a taxa pode estar em tabelas); ativar é direto.
  const requestToggle = (item) => {
    if (isActive(item.status)) setToToggle(item);
    else applyToggle(item);
  };

  const addComponent = async (component) => {
    if (!editing) return;
    try {
      setMappingBusyId(`add-${component.id}`);
      await api.post(`${URL_BASE}/${editing.id}/componentes`, { idComponentePrestacaoServico: component.id });
      setCandidates((items) => items.filter((item) => item.id !== component.id));
      await loadLinked(editing.id);
      loadTaxas();
    } catch (error) {
      toastError(addToast, "Erro ao vincular", error, "Não foi possível vincular o componente.");
    } finally {
      setMappingBusyId(null);
    }
  };

  const removeComponent = async (component) => {
    if (!editing) return;
    try {
      setMappingBusyId(`remove-${component.id}`);
      await api.delete(`${URL_BASE}/${editing.id}/componentes/${component.id}`);
      await loadLinked(editing.id);
      loadTaxas();
    } catch (error) {
      toastError(addToast, "Erro ao remover vínculo", error, "Não foi possível remover o componente.");
    } finally {
      setMappingBusyId(null);
    }
  };

  const addBaseComponent = async (candidate) => {
    if (!editing) return;
    try {
      setBaseMappingBusyId(`add-${candidate.id}`);
      await api.post(`${URL_BASE}/${editing.id}/base-calculo`, { idFreteTaxaComponente: candidate.id });
      setCandidatesBase((items) => items.filter((item) => item.id !== candidate.id));
      await loadLinkedBase(editing.id);
    } catch (error) {
      toastError(addToast, "Erro ao vincular", error, "Não foi possível vincular a taxa à base de cálculo.");
    } finally {
      setBaseMappingBusyId(null);
    }
  };

  const removeBaseComponent = async (candidate) => {
    if (!editing) return;
    try {
      setBaseMappingBusyId(`remove-${candidate.id}`);
      await api.delete(`${URL_BASE}/${editing.id}/base-calculo/${candidate.id}`);
      await loadLinkedBase(editing.id);
    } catch (error) {
      toastError(addToast, "Erro ao remover vínculo", error, "Não foi possível remover a taxa da base de cálculo.");
    } finally {
      setBaseMappingBusyId(null);
    }
  };

  const exportar = () =>
    exportCsv({
      filename: "taxas_frete",
      columns: [
        { key: "codigo", label: "Código" },
        { key: "nome", label: "Taxa" },
        { key: "descricao", label: "Descrição" },
        { label: "Tipo", getValue: (row) => TIPO_LABEL[row.tipo] || row.tipo || "" },
        { key: "por", label: "Incide sobre" },
        { label: "Generalidade", getValue: (row) => (row.generalidade ? "Sim" : "Não") },
        { label: "Aplica sobre tipo de veículo", getValue: (row) => (row.aplicaSobreTipoVeiculo ? "Sim" : "Não") },
        { label: "Validade inicial", getValue: (row) => dataBr(row.dataValidadeInicio) },
        { label: "Validade final", getValue: (row) => dataBr(row.dataValidadeFim) },
        { label: "Componentes do CT-e", getValue: (row) => Number(row.quantidadeComponentes) || 0 },
        { label: "Tabelas", getValue: (row) => Number(row.quantidadeTabelas) || 0 },
        { label: "Status", getValue: (row) => (isActive(row.status) ? "Ativa" : "Inativa") },
      ],
      rows: sorted,
    });

  const handleSort = useCallback((field) => setSort((prev) => toggleSort(prev, field)), []);
  const clearFilters = () => {
    setSearch("");
    setStatusFilter(STATUS_ALL);
    setTipoFilter("");
    setComponentesFilter("");
  };
  const activeFiltersCount = [statusFilter !== STATUS_ALL, Boolean(tipoFilter), Boolean(componentesFilter)].filter(Boolean).length;
  const hasFilters = normalizeText(search) !== "" || activeFiltersCount > 0;
  const showState = loading || loadError || sorted.length === 0;

  const renderBusca = ({ id, label, value, onChange }) => (
    <FormGroup>
      <Label for={id}>{label}</Label>
      <InputGroup>
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Pesquise por código ou nome (mínimo 2 caracteres)" />
        <InputGroupAddon addonType="append">
          <InputGroupText>
            <FaSearch />
          </InputGroupText>
        </InputGroupAddon>
      </InputGroup>
    </FormGroup>
  );

  const renderVinculados = ({ titulo, itens, carregando, vazio, comRegra, busyId, onRemove }) => (
    <>
      <div className="cg-secao-titulo">{titulo}</div>
      <Table responsive size="sm" className="cg-table">
        <colgroup>
          <col style={{ width: 120 }} />
          <col />
          <col style={{ width: 100 }} />
          <col style={{ width: 52 }} />
        </colgroup>
        <thead>
          <tr>
            <th>Código</th>
            <th>{comRegra ? "Taxa" : "Componente do CT-e"}</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {carregando ? (
            <tr>
              <td colSpan="4" style={{ textAlign: "center", padding: "1.5rem" }}>
                <Spinner size="sm" /> Carregando...
              </td>
            </tr>
          ) : null}
          {!carregando &&
            itens.map((item) => (
              <tr key={item.id}>
                <td className="cg-mono">{item.codigo || "-"}</td>
                <td>
                  {item.nome}
                  {comRegra ? <span className="cg-sub">{regraLabel(item)}</span> : null}
                </td>
                <td>
                  <StatusPill active={isActive(item.status)} />
                </td>
                <td style={{ textAlign: "right" }}>
                  <Button
                    color="danger"
                    outline
                    size="sm"
                    title="Remover vínculo"
                    style={iconButtonStyle}
                    disabled={Boolean(busyId)}
                    onClick={() => onRemove(item)}
                  >
                    <FaTimes />
                  </Button>
                </td>
              </tr>
            ))}
          {!carregando && !itens.length ? (
            <tr>
              <td colSpan="4" style={{ textAlign: "center", padding: "1.5rem", ...mutedStyle }}>
                {vazio}
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </>
  );

  const renderCandidatos = ({ itens, carregando, textoCarregando, comRegra, busyId, titulo, onAdd }) => (
    <>
      {carregando ? (
        <div style={{ padding: "0.75rem", ...mutedStyle }}>
          <Spinner size="sm" /> {textoCarregando}
        </div>
      ) : null}
      {!carregando && itens.length ? (
        <div className="cg-busca-resultados">
          <Table size="sm" responsive className="cg-table" style={{ marginBottom: 0 }}>
            <colgroup>
              <col style={{ width: 120 }} />
              <col />
              <col style={{ width: 52 }} />
            </colgroup>
            <tbody>
              {itens.map((item) => (
                <tr key={item.id}>
                  <td className="cg-mono">{item.codigo || "-"}</td>
                  <td>
                    {item.nome}
                    {comRegra ? <span className="cg-sub">{regraLabel(item)}</span> : null}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Button color="info" size="sm" title={titulo} style={iconButtonStyle} disabled={Boolean(busyId)} onClick={() => onAdd(item)}>
                      <FaPlus />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}
    </>
  );

  const tabLink = (tab, label, disabled) => (
    <NavItem>
      <NavLink
        href="#"
        active={activeTab === tab}
        disabled={disabled}
        onClick={(event) => {
          event.preventDefault();
          if (!disabled) setActiveTab(tab);
        }}
      >
        {label}
      </NavLink>
    </NavItem>
  );

  return (
    <div className="content taxas-frete-page">
      <Modal isOpen={modalOpen} toggle={closeModal} modalClassName="modal-align-top" fade={false} size="lg">
        <ModalHeader toggle={closeModal} tag="h5">
          {editing ? `Taxa ${form.nome || form.codigo}` : "Nova taxa de frete"}
          <div className="cg-modal-subtitle" style={{ marginBottom: 0, fontWeight: 400 }}>
            Cadastro global e associação com os componentes recebidos no CT-e
          </div>
        </ModalHeader>

        <Nav tabs className="cg-tabs">
          {tabLink("dados", "Dados da taxa", false)}
          {tabLink("componentes", `Componentes do CT-e${linked.length ? ` (${linked.length})` : ""}`, !editing)}
          {isComposta ? tabLink("baseCalculo", `Base de cálculo${linkedBase.length ? ` (${linkedBase.length})` : ""}`, !editing) : null}
        </Nav>

        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          {!editing && activeTab === "dados" ? (
            <div className="cg-dica">Salve a taxa para liberar o vínculo com os componentes do CT-e.</div>
          ) : null}
          {activeTab === "dados" ? (
            <>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <Label for="taxa-codigo">Código</Label>
                    <Input id="taxa-codigo" value={form.codigo} maxLength={10} invalid={Boolean(errors.codigo)} onChange={(e) => updateForm("codigo", e.target.value)} />
                    {errors.codigo ? <FormFeedback>{errors.codigo}</FormFeedback> : null}
                  </FormGroup>
                </Col>
                <Col md="9">
                  <FormGroup>
                    <Label for="taxa-nome">Nome</Label>
                    <Input id="taxa-nome" value={form.nome} maxLength={60} invalid={Boolean(errors.nome)} onChange={(e) => updateForm("nome", e.target.value)} />
                    {errors.nome ? <FormFeedback>{errors.nome}</FormFeedback> : null}
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label for="taxa-descricao">Descrição (opcional)</Label>
                <Input id="taxa-descricao" type="textarea" rows="2" maxLength={255} value={form.descricao} onChange={(e) => updateForm("descricao", e.target.value)} />
              </FormGroup>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="taxa-tipo">Tipo</Label>
                    <Input id="taxa-tipo" type="select" value={form.tipo} invalid={Boolean(errors.tipo)} onChange={(e) => updateForm("tipo", e.target.value)}>
                      <option value="">Selecione</option>
                      {Object.keys(TIPO_POR).map((tipo) => (
                        <option key={tipo} value={tipo}>
                          {TIPO_LABEL[tipo]}
                        </option>
                      ))}
                    </Input>
                    {errors.tipo ? <FormFeedback>{errors.tipo}</FormFeedback> : null}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="taxa-por">Incide sobre</Label>
                    <Input id="taxa-por" type="select" value={form.por} disabled={!form.tipo} invalid={Boolean(errors.por)} onChange={(e) => updateForm("por", e.target.value)}>
                      <option value="">{form.tipo ? "Selecione" : "Escolha o tipo antes"}</option>
                      {(TIPO_POR[form.tipo] || []).map((por) => (
                        <option key={por} value={por}>
                          {por}
                        </option>
                      ))}
                    </Input>
                    {errors.por ? <FormFeedback>{errors.por}</FormFeedback> : null}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="taxa-status">Status</Label>
                    <Input id="taxa-status" type="select" value={form.status ? "active" : "inactive"} onChange={(e) => updateForm("status", e.target.value === "active")}>
                      <option value="active">Ativa</option>
                      <option value="inactive">Inativa</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              {isComposta ? (
                <div className="cg-dica">
                  Base composta: depois de salvar, escolha na aba "Base de cálculo" as taxas que somam ao custo do frete (ex.: GRIS,
                  Pedágio).
                </div>
              ) : null}
              <Row>
                <Col md="4">
                  <FormGroup check>
                    <Label check>
                      <Input type="checkbox" checked={form.generalidade} onChange={(e) => updateForm("generalidade", e.target.checked)} />
                      <span className="form-check-sign" />
                      Generalidade
                    </Label>
                  </FormGroup>
                </Col>
                <Col md="8">
                  <FormGroup check>
                    <Label check>
                      <Input type="checkbox" checked={form.aplicaSobreTipoVeiculo} onChange={(e) => updateForm("aplicaSobreTipoVeiculo", e.target.checked)} />
                      <span className="form-check-sign" />
                      Aplica sobre tipo de veículo
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              <Row style={{ marginTop: "0.75rem" }}>
                <Col md="4">
                  <FormGroup>
                    <Label for="taxa-validade-inicio">Validade inicial</Label>
                    <Input id="taxa-validade-inicio" type="date" value={form.dataValidadeInicio} onChange={(e) => updateForm("dataValidadeInicio", e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="taxa-validade-fim">Validade final</Label>
                    <Input
                      id="taxa-validade-fim"
                      type="date"
                      value={form.dataValidadeFim}
                      invalid={Boolean(errors.dataValidadeFim)}
                      onChange={(e) => updateForm("dataValidadeFim", e.target.value)}
                    />
                    {errors.dataValidadeFim ? <FormFeedback>{errors.dataValidadeFim}</FormFeedback> : null}
                  </FormGroup>
                </Col>
              </Row>
              <div className="cg-modal-footer">
                <Button color="default" outline size="sm" onClick={closeModal} disabled={saving}>
                  {editing ? "Fechar" : "Cancelar"}
                </Button>
                <Button color="info" size="sm" onClick={save} disabled={saving} data-testid="salvar">
                  {saving ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </>
          ) : activeTab === "componentes" ? (
            <>
              <div className="cg-dica">
                Os componentes do CT-e vinculados aqui ficam associados a esta taxa.
              </div>
              {renderBusca({ id: "component-search", label: "Adicionar componente do CT-e", value: componentSearch, onChange: setComponentSearch })}
              {renderCandidatos({
                itens: candidates,
                carregando: candidateLoading,
                textoCarregando: "Pesquisando componentes...",
                comRegra: false,
                busyId: mappingBusyId,
                titulo: "Vincular componente",
                onAdd: addComponent,
              })}
              {renderVinculados({
                titulo: "Componentes vinculados",
                itens: linked,
                carregando: linkedLoading,
                vazio: "Nenhum componente vinculado a esta taxa.",
                comRegra: false,
                busyId: mappingBusyId,
                onRemove: removeComponent,
              })}
            </>
          ) : (
            <>
              <div className="cg-dica">A base do percentual é o custo do frete + a soma das taxas vinculadas abaixo (ex.: GRIS, Pedágio).</div>
              {renderBusca({ id: "base-calculo-search", label: "Adicionar taxa à base de cálculo", value: baseSearch, onChange: setBaseSearch })}
              {renderCandidatos({
                itens: candidatesBase,
                carregando: candidateBaseLoading,
                textoCarregando: "Pesquisando taxas...",
                comRegra: true,
                busyId: baseMappingBusyId,
                titulo: "Vincular à base de cálculo",
                onAdd: addBaseComponent,
              })}
              {renderVinculados({
                titulo: "Taxas vinculadas à base",
                itens: linkedBase,
                carregando: linkedBaseLoading,
                vazio: "Nenhuma taxa vinculada à base de cálculo.",
                comRegra: true,
                busyId: baseMappingBusyId,
                onRemove: removeBaseComponent,
              })}
            </>
          )}
        </ModalBody>
      </Modal>

      <ConfirmModal
        isOpen={Boolean(toToggle)}
        title="Inativar taxa"
        confirmLabel="Inativar"
        busyLabel="Inativando..."
        busy={toggling}
        onConfirm={() => applyToggle(toToggle)}
        onCancel={() => setToToggle(null)}
      >
        Inativar a taxa <strong>{toToggle ? toToggle.nome : ""}</strong>?
        {toToggle && Number(toToggle.quantidadeTabelas) > 0
          ? ` Ela está em ${toToggle.quantidadeTabelas} tabela(s) de frete.`
          : ""}{" "}
        Dá para ativar de novo a qualquer momento.
      </ConfirmModal>

      <PageHero
        eyebrow="Cadastros globais"
        title="Taxas de frete"
        description="Cadastre as taxas usadas nas tabelas de frete (ex.: GRIS, pedágio) e relacione os nomes recebidos nos componentes do CT-e."
        actions={
          <>
            <PageHeroRefreshButton onClick={loadTaxas} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={loading || sorted.length === 0} />
            <Button color="info" className="btn-sm" onClick={openCreate}>
              <FaPlus style={{ marginRight: 6 }} />
              Nova taxa
            </Button>
          </>
        }
      />

      <MetricCardGroup>
        <MetricCard
          label="Taxas cadastradas"
          value={totais.total}
          helper="clique para ver todas"
          iconClassName="tim-icons icon-coins"
          tone="info"
          onClick={clearFilters}
          active={!hasFilters}
        />
        <MetricCard
          label="Ativas"
          value={totais.ativas}
          helper="disponíveis para as tabelas"
          iconClassName="tim-icons icon-check-2"
          tone="success"
          onClick={() => setStatusFilter((c) => (c === "active" ? STATUS_ALL : "active"))}
          active={statusFilter === "active" && !componentesFilter}
        />
        <MetricCard
          label="Ativas sem componente do CT-e"
          value={totais.semComponente}
          helper="nenhum componente do CT-e associado"
          iconClassName="tim-icons icon-alert-circle-exc"
          tone="warning"
          onClick={() => {
            const ligado = componentesFilter === "sem" && statusFilter === "active";
            setComponentesFilter(ligado ? "" : "sem");
            setStatusFilter(ligado ? STATUS_ALL : "active");
          }}
          active={componentesFilter === "sem" && statusFilter === "active"}
        />
        <MetricCard
          label="Usadas em tabelas"
          value={totais.emTabelas}
          helper="presentes em ao menos uma tabela"
          iconClassName="tim-icons icon-money-coins"
          tone="neutral"
        />
      </MetricCardGroup>

      <FilterBand
        mode="client"
        searchLabel="Buscar taxa"
        searchHint="Código, nome, descrição, tipo ou incidência"
        searchPlaceholder="Buscar por código, nome, descrição, tipo ou incidência"
        searchValue={search}
        onSearchChange={setSearch}
        activeFiltersCount={activeFiltersCount}
        panelTitle="Filtros"
        panelDescription="Refine por status, tipo da taxa e vínculo com componentes do CT-e."
        onClear={hasFilters ? clearFilters : undefined}
        showActions={hasFilters}
      >
        <Row>
          <Col md="4">
            <Label for="taxas-status" style={{ color: AUTOPILOT_COLORS.muted }}>
              Status
            </Label>
            <Select
              inputId="taxas-status"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((o) => o.value === statusFilter) || STATUS_OPTIONS[0]}
              onChange={(opt) => setStatusFilter(opt ? opt.value : STATUS_ALL)}
              isSearchable={false}
            />
          </Col>
          <Col md="4">
            <Label for="taxas-tipo" style={{ color: AUTOPILOT_COLORS.muted }}>
              Tipo
            </Label>
            <Select
              inputId="taxas-tipo"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={TIPO_OPTIONS}
              value={TIPO_OPTIONS.find((o) => o.value === tipoFilter) || TIPO_OPTIONS[0]}
              onChange={(opt) => setTipoFilter(opt ? opt.value : "")}
              isSearchable={false}
            />
          </Col>
          <Col md="4">
            <Label for="taxas-componentes" style={{ color: AUTOPILOT_COLORS.muted }}>
              Componentes do CT-e
            </Label>
            <Select
              inputId="taxas-componentes"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={COMPONENTES_OPTIONS}
              value={COMPONENTES_OPTIONS.find((o) => o.value === componentesFilter) || COMPONENTES_OPTIONS[0]}
              onChange={(opt) => setComponentesFilter(opt ? opt.value : "")}
              isSearchable={false}
            />
          </Col>
        </Row>
      </FilterBand>

      <ShellCard title="Taxas de frete cadastradas" description={`${sorted.length} resultado(s)`}>
        {showState ? (
          <ListState
            loading={loading}
            error={loadError}
            empty={sorted.length === 0}
            loadingText="Carregando taxas…"
            emptyTitle="Nenhuma taxa encontrada"
            emptyText={hasFilters ? "Ajuste os filtros ou limpe a busca." : "Cadastre a primeira taxa pelo botão acima."}
            onRetry={loadTaxas}
          />
        ) : (
          <TableSurface>
            <Table size="sm" responsive hover className="cg-table hub-thead-accent" style={{ minWidth: 980 }} data-testid="taxas-tabela">
              <colgroup>
                <col style={{ width: 110 }} />
                <col />
                <col style={{ width: 200 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 96 }} />
              </colgroup>
              <thead className="text-primary">
                <tr>
                  <th>
                    <SortHeader label="Código" field="codigo" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Taxa" field="nome" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Regra" field="regra" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Componentes do CT-e" field="quantidadeComponentes" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Tabelas" field="quantidadeTabelas" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paged.items.map((item) => {
                  const ativa = isActive(item.status);
                  const qtdComp = Number(item.quantidadeComponentes) || 0;
                  return (
                    <tr key={item.id}>
                      <td className="cg-mono">{item.codigo}</td>
                      <td>
                        <strong>{item.nome}</strong>
                        {item.descricao ? <span className="cg-sub">{item.descricao}</span> : null}
                      </td>
                      <td>
                        {regraLabel(item)}
                        <span className="cg-sub">
                          {[item.generalidade ? "Generalidade" : null, item.aplicaSobreTipoVeiculo ? "Por tipo de veículo" : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                        {item.dataValidadeFim ? (
                          vencida(item) ? (
                            <Pill tone="warning" title="A validade final já passou">
                              Vencida em {dataBr(item.dataValidadeFim)}
                            </Pill>
                          ) : (
                            <span className="cg-sub">Válida até {dataBr(item.dataValidadeFim)}</span>
                          )
                        ) : null}
                      </td>
                      <td>
                        <Button
                          color={qtdComp ? "info" : "warning"}
                          outline
                          size="sm"
                          onClick={() => openEdit(item, "componentes")}
                          title={qtdComp ? "Ver os componentes vinculados" : "Nenhum componente vinculado: clique para vincular"}
                        >
                          <FaLink style={{ marginRight: 6 }} />
                          {qtdComp}
                        </Button>
                      </td>
                      <td>{Number(item.quantidadeTabelas) || 0}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => requestToggle(item)}
                          title={ativa ? "Clique para inativar" : "Clique para ativar"}
                          data-testid={`status-${item.id}`}
                        >
                          <StatusPill active={ativa} activeLabel="Ativa" inactiveLabel="Inativa" />
                        </button>
                      </td>
                      <td className="text-right">
                        <span className="cg-acoes">
                          <Button color="link" size="sm" className="btn-icon" title="Editar taxa" onClick={() => openEdit(item)}>
                            <FaPen />
                          </Button>
                          <Button color="link" size="sm" className="btn-icon" title="Gerenciar componentes do CT-e" onClick={() => openEdit(item, "componentes")}>
                            <FaLink />
                          </Button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              total={paged.total}
              itemLabel="taxa(s)"
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </TableSurface>
        )}
      </ShellCard>
    </div>
  );
};

export default TaxasFrete;
